import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useImageBaseUrl } from "./nyHelpers";

// Template71: Plan de viaje NY (guia-ny Page2)
// Órdenes de página en la guía NY: 0=portada, 1=plan, 2=midtown-1, ...
const diaOrderIndex = [2, 4, 7, 12, 15, 17, 20];
const extraOrderIndex = [22, 23, 24, 25, 27, 29];

const Template71 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onNavigateToPage,
	pages = [],
}) => {
	const BASE = useImageBaseUrl();
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const defaultDias = useMemo(
		() => [
			{
				image: `${BASE}times-square-sticker.png`,
				titulo: "Midtown Manhattan",
				tituloMovil: "Midtown Manhattan",
			},
			{
				image: `${BASE}estatua-sticker.png`,
				titulo: "Lower Manhattan & Estatua de la Libertad",
				tituloMovil: "Lower Manhattan",
			},
			{
				image: `${BASE}brooklyn-sticker.png`,
				titulo: "Brooklyn, Chinatown & Little Italy",
				tituloMovil: "Brooklyn y Chinatown",
			},
			{
				image: `${BASE}central-park.png`,
				titulo: "Central Park y Upper Manhattan",
				tituloMovil: "Upper Manhattan",
			},
			{
				image: `${BASE}empire-sticker-menu.png`,
				titulo: "Del Empire State al SoHo",
				tituloMovil: "Del Empire State al SoHo",
			},
			{
				image: `${BASE}vessel-sticker-menu.png`,
				titulo: "Hudson Yards y Chelsea",
				tituloMovil: "Hudson Yards y Chelsea",
			},
			{
				image: `${BASE}washington-sticker-menu.png`,
				titulo: "Día libre o excursión",
				tituloMovil: "Día libre o excursión",
			},
		],
		[BASE],
	);
	const defaultExtras = useMemo(
		() => [
			{ image: `${BASE}subway-sticker-menu.png`, titulo: "Metro NY" },
			{ image: `${BASE}shops-menu.png`, titulo: "Tiendas originales" },
			{ image: `${BASE}airport-sticker.png`, titulo: "Aeropuertos" },
			{ image: `${BASE}pases.png`, titulo: "Pases" },
			{ image: `${BASE}consejos-stickers.png`, titulo: "Consejos útiles" },
		],
		[BASE],
	);

	// Preferir imágenes de la página (el preview las firma). Sin eso, <img> pide
	// el asset sin token y el backend lo bloquea si no hay sesión.
	const dias = useMemo(() => {
		const fromPage = page?.dias ?? page?.data?.dias;
		if (!Array.isArray(fromPage) || fromPage.length === 0) return defaultDias;
		return fromPage.map((dia, index) => ({
			...defaultDias[index],
			...dia,
		}));
	}, [page, defaultDias]);
	const extras = useMemo(() => {
		const fromPage = page?.extras ?? page?.data?.extras;
		if (!Array.isArray(fromPage) || fromPage.length === 0) return defaultExtras;
		return fromPage.map((extra, index) => ({
			...defaultExtras[index],
			...extra,
		}));
	}, [page, defaultExtras]);
	const logoImage =
		page?.canvasImages?.find((img) => img.id === "logo")?.imageUrl ||
		`${BASE}logo.png`;

	const goToPageByOrder = (order) => {
		if (!onNavigateToPage || !pages.length) return;
		const p = pages.find(
			(pg) =>
				(pg.data?.order !== undefined ? pg.data.order : pg.order) === order,
		);
		if (p) onNavigateToPage(p.id ?? p.data?.id);
	};

	const extrasList = isSmallScreen ? [...extras] : extras.slice(0, 5);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: (fontFamily || '"Poppins", sans-serif') + "!important",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 700,
					position: "sticky",
					top: 0,
				}}
			>
				Plan de viaje
				<img
					src={logoImage}
					alt="logo"
					style={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 10539,
					}}
				/>
			</div>

			<div data-block-id="menu" style={{ padding: isSmallScreen ? "10px 10px" : "20px 16px" }}>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: isSmallScreen ? 10 : 20,
						flexWrap: "nowrap",
					}}
				>
					{dias.map((dia, index) => (
						<div
							key={index}
							style={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: isSmallScreen ? "5px" : "10px",
								position: "relative",
								minHeight: 30,
								width: "auto",
								cursor: onNavigateToPage ? "pointer" : "default",
								transition: "transform 0.3s ease",
								marginLeft: isSmallScreen ? 20 : 20,
							}}
							onClick={() => goToPageByOrder(diaOrderIndex[index])}
							onMouseEnter={(e) => {
								if (onNavigateToPage) {
									e.currentTarget.style.transform = "translateY(-5px)";
									const box = e.currentTarget.querySelector(".box");
									if (box)
										box.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
								}
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.transform = "translateY(0)";
								const box = e.currentTarget.querySelector(".box");
								if (box) box.style.boxShadow = "none";
							}}
						>
							<img
								src={dia.image}
								alt=""
								style={{
									width: 45,
									zIndex: 100,
									position: "absolute",
									left: -20,
								}}
							/>
							<div
								className="box"
								style={{
									backgroundColor: "#fff",
									borderRadius: "12px",
									padding: "18px 16px",
									display: "flex",
									alignItems: "center",
									gap: "10px",
									position: "relative",
									minHeight: 30,
									width: "100%",
									marginLeft: 0,
									textAlign: "left",
									height: "auto",
									flex: 1,
									transition: "box-shadow 0.3s ease",
									paddingLeft: "16px",
								}}
							>
								<div
									style={{
										fontSize: "13px",
										fontWeight: 600,
										color: "#333",
										marginLeft: "20px",
										whiteSpace:
											isSmallScreen && dia.tituloMovil ? "pre-line" : "normal",
									}}
								>
									Día {index + 1}:{" "}
									{isSmallScreen && dia.tituloMovil
										? typeof dia.tituloMovil === "string"
											? dia.tituloMovil
											: dia.titulo
										: dia.titulo}
								</div>
							</div>
						</div>
					))}
				</div>

				<h3
					style={{
						fontFamily: '"Poppins", sans-serif',
						fontSize: 12,
						fontWeight: 700,
						margin: 0,
						textTransform: "uppercase",
						marginTop: isSmallScreen ? 8 : 16,
						marginBottom: 8,
					}}
				>
					Información útil
				</h3>

				<div
					style={{
						display: "flex",
						flexDirection: isSmallScreen ? "column" : "row",
						gap: isSmallScreen ? "20px" : "8px",
						flexWrap: isSmallScreen ? "nowrap" : "nowrap",
						paddingBottom: "2px",
						overflowX: isSmallScreen ? "visible" : "auto",
						overflowY: "visible",
					}}
				>
					{(isSmallScreen ? extras : extrasList).map((extra, index) => (
						<div
							key={index}
							style={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: "10px",
								position: "relative",
								minHeight: 30,
								width: isSmallScreen ? "auto" : "calc(20% - 7px)",
								cursor: onNavigateToPage ? "pointer" : "default",
								transition: "transform 0.3s ease",
								marginLeft: isSmallScreen ? 20 : 0,
								flexShrink: isSmallScreen ? 0 : 0,
							}}
							onClick={() => goToPageByOrder(extraOrderIndex[index])}
							onMouseEnter={(e) => {
								if (onNavigateToPage) {
									e.currentTarget.style.transform = "translateY(-5px)";
									const box = e.currentTarget.querySelector(".box");
									if (box)
										box.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
								}
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.transform = "translateY(0)";
								const box = e.currentTarget.querySelector(".box");
								if (box) box.style.boxShadow = "none";
							}}
						>
							{isSmallScreen ? (
								<>
									<img
										src={extra.image}
										alt=""
										style={{
											width: 45,
											zIndex: 100,
											position: "absolute",
											left: -20,
										}}
									/>
									<div
										className="box"
										style={{
											backgroundColor: "#fff",
											borderRadius: "12px",
											padding: "18px 16px",
											display: "flex",
											alignItems: "center",
											gap: "10px",
											position: "relative",
											minHeight: 30,
											width: "100%",
											marginLeft: 0,
											textAlign: "left",
											height: "auto",
											flex: 1,
											transition: "box-shadow 0.3s ease",
											paddingLeft: "16px",
										}}
									>
										<div
											style={{
												fontSize: "13px",
												fontWeight: 600,
												color: "#333",
												marginLeft: "20px",
											}}
										>
											{extra.titulo}
										</div>
									</div>
								</>
							) : (
								<>
									<div
										style={{
											backgroundColor: "#fff",
											borderRadius: "12px",
											width: "100%",
											padding: "10px 8px",
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											textAlign: "center",
											justifyContent: "center",
											transition: "box-shadow 0.3s ease",
										}}
										className="box"
									>
										<div style={{ fontSize: "24px", marginBottom: 6 }}>
											<img
												src={extra.image}
												alt=""
												style={{
													width: 38,
													height: 57,
													zIndex: 100,
													objectFit: "contain",
												}}
											/>
										</div>
										<div
											style={{
												fontSize: "12px",
												fontWeight: 700,
												color: "#333",
												lineHeight: 1.3,
												marginTop: -15,
											}}
										>
											{extra.titulo}
										</div>
									</div>
								</>
							)}
						</div>
					))}
				</div>
			</div>
		</Box>
	);
};

export default Template71;
